import { useCustomerDetails, useWealthOverview } from '@egw/api'
import {
  Button,
  ButtonVariant,
  GenericError,
  PageHeader,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  useModalState,
} from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { AccountTransferDrawer } from '../../components/account-transfer-drawer/account-transfer-drawer'
import { type TransferListOption } from '../../types/form-model/form-model'
import {
  buildTransferAccountOption,
  checkIfInvestmentAccount,
  getAccountTypeFromTypename,
} from '../../utils/transfer-utils/transfer-utils'
import { TabbedReceiveInvestmentWire } from './tabbed-receive-investment-wire'
import { TabbedSendInvestmentWire } from './tabbed-send-investment-wire'

export enum WIRE_TRANSFSER_TAB {
  _RECEIVE = 'RECEIVE',
  _SEND = 'SEND',
}

export enum VIEW_ENUM {
  _TABBED_VIEW = 'TABBED_VIEW',
  _SUCCESS_VIEW = 'SUCCESS_VIEW',
}

export const TabbedWireTransfer: FC = () => {
  const { t } = useTranslation('features-transfer')

  const navigate = useNavigate()

  const [view, setView] = useState<VIEW_ENUM>(VIEW_ENUM._TABBED_VIEW)

  const [accountSelected, setAccountSelected] = useState<TransferListOption | undefined>(undefined)

  const { openModal, closeModal, isOpen } = useModalState()

  const { flags } = useFlags<FeatureFlags>()

  const { data: overviewData, error: overviewError, isLoading: overviewIsLoading } = useWealthOverview()

  const apexDepositableAccounts = useMemo(() => {
    return (
      overviewData?.personalPortfolio?.accounts
        .filter((account) => {
          const accountType = getAccountTypeFromTypename(account.__typename)
          return checkIfInvestmentAccount(accountType)
        })
        .filter((account) => 'attributes' in account && account.attributes?.clientWithdrawalAllowed) ?? []
    )
  }, [overviewData])

  const accountsAvailableToSelect = useMemo(() => {
    return apexDepositableAccounts
  }, [apexDepositableAccounts])

  const hasMultipleAccounts = useMemo(() => accountsAvailableToSelect.length > 1, [accountsAvailableToSelect])

  // Set the default account to select based on the type of account available
  useEffect(() => {
    if (hasMultipleAccounts) return
    if (apexDepositableAccounts.length > 0) {
      setAccountSelected(
        buildTransferAccountOption({
          account: accountsAvailableToSelect[0],
          t,
        }),
      )
    }
  }, [accountsAvailableToSelect, apexDepositableAccounts, hasMultipleAccounts, t])

  const setSuccessView = useCallback((): void => {
    setView(VIEW_ENUM._SUCCESS_VIEW)
  }, [])

  const {
    data: customerDetails,
    error: customerDetailsError,
    isLoading: customerDetailsIsLoading,
  } = useCustomerDetails()

  const isLoading = overviewIsLoading || customerDetailsIsLoading
  const isError = overviewError ?? customerDetailsError

  return (
    <>
      <PageHeader
        backLinkUrl={WEALTH_ROUTES.TRANSFER_WIRE.backPath}
        subtitle={t('transfer.title')}
        title={t('tabbed_wire_transfer.title')}
      />
      {isLoading && !isError ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}
      {!isLoading && !isError && overviewData ? (
        <div className="egw-container">
          {view === VIEW_ENUM._TABBED_VIEW ? (
            <Tabs defaultSelectedTab={WIRE_TRANSFSER_TAB._RECEIVE}>
              <TabList>
                <Tab id={WIRE_TRANSFSER_TAB._RECEIVE} label={t('tabbed_wire_transfer.receive_transfer.tab_title')} />
                {flags.rolloutApexSendWire ? (
                  <Tab id={WIRE_TRANSFSER_TAB._SEND} label={t('tabbed_wire_transfer.send_transfer.tab_title')} />
                ) : null}
              </TabList>
              <TabPanel id={WIRE_TRANSFSER_TAB._RECEIVE}>
                <TabbedReceiveInvestmentWire
                  hasMultipleAccounts={hasMultipleAccounts}
                  accountSelected={accountSelected}
                  isLoading={isLoading}
                  openModal={openModal}
                />
              </TabPanel>
              {flags.rolloutApexSendWire ? (
                <TabPanel id={WIRE_TRANSFSER_TAB._SEND}>
                  <TabbedSendInvestmentWire setSuccessView={setSuccessView} />
                </TabPanel>
              ) : null}
            </Tabs>
          ) : null}
          {view === VIEW_ENUM._SUCCESS_VIEW ? (
            <div className="flex flex-col max-md:justify-between gap-2 h-full">
              <div className="flex flex-col gap-md">
                <div className="flex flex-col gap-sm">
                  <CheckCircleIcon className="h-12 w-12 stroke-textSuccess stroke-[.5px]" />
                  <p className="typo-primary-xl-light text-text-header-success">
                    {t('tabbed_wire_transfer.success_view.title')}
                  </p>
                </div>
                <p className="text-text-meta typo-primary-base-regular">{t('tabbed_wire_transfer.success_view.p1')}</p>
                <p className="text-text-meta typo-primary-base-regular">{t('tabbed_wire_transfer.success_view.p2')}</p>
              </div>
              <Button
                id="wireSuccessDone"
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
                className="md:w-fit md:mt-8"
              >
                {t('tabbed_wire_transfer.success_view.cta')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {isError ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
      <AccountTransferDrawer
        isBankAccount={false}
        isModalOpen={isOpen}
        onClose={closeModal}
        options={accountsAvailableToSelect}
        onSelect={(option) => {
          setAccountSelected(option)
          closeModal()
        }}
      />
    </>
  )
}

interface AccountNumberDisplayProps {
  showAccountSelector: boolean
  accountSelected?: TransferListOption
  accountNumber?: string
}

export const AccountNumberDisplay: FC<AccountNumberDisplayProps> = ({
  showAccountSelector,
  accountSelected,
  accountNumber,
}) => {
  const { t } = useTranslation('features-transfer')

  // Determine if we should show the account number value
  const shouldShowAccountValue = !(showAccountSelector && !accountSelected)

  return (
    <div className="flex flex-row">
      <p className="typo-primary-base text-text-meta w-1/3">
        {t('tabbed_wire_transfer.receive_transfer.account_number_label')}
      </p>
      {shouldShowAccountValue ? (
        <div className="flex flex-row gap-xs items-center">
          <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">{accountNumber}</p>
        </div>
      ) : (
        <p className="typo-primary-base-semibold text-text-header w-2/3">
          {t('tabbed_wire_transfer.receive_transfer.select_account')}
        </p>
      )}
    </div>
  )
}
